import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '../../../components/layout/AdminLayout'

export default function AdminAgenciesIndex({ agencies, pagination, filters, stats }) {
  const [searchQuery, setSearchQuery] = useState(filters.q || '')
  const [selectedFilter, setSelectedFilter] = useState(filters.filter || '')

  const handleSearch = (e) => {
    e.preventDefault()
    applyFilters({ q: searchQuery })
  }

  const applyFilters = (newFilters) => {
    router.get('/admin/agencies', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedFilter('')
    router.get('/admin/agencies', {}, { preserveState: true })
  }

  const hasActiveFilters = filters.q || filters.filter

  return (
    <AdminLayout>
      <Head title="Agences - Administration" />

      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Agences</h1>
            <p className="mt-2 text-sm text-gray-600">
              Gestion des agences immobilières du réseau
            </p>
          </div>
          <Link
            href="/admin/agencies/new"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nouvelle agence
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <StatsCard title="Total agences" value={stats.totalAgencies} color="blue" />
          <StatsCard title="Actives" value={stats.activeAgencies} color="green" />
          <StatsCard title="Total leads" value={stats.totalLeads} color="indigo" />
          <StatsCard title="Total utilisateurs" value={stats.totalUsers} color="gray" />
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtres et recherche</h2>
          <form onSubmit={handleSearch} className="space-y-6">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher une agence
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Nom, ville ou email..."
                  className="flex-1 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Rechercher
                </button>
              </div>
            </div>

            {/* Filters Row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Status Filter */}
              <div>
                <label htmlFor="filter" className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  id="filter"
                  value={selectedFilter}
                  onChange={(e) => {
                    setSelectedFilter(e.target.value)
                    applyFilters({ filter: e.target.value })
                  }}
                  className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                >
                  <option value="">Toutes les agences</option>
                  <option value="active">Actives uniquement</option>
                  <option value="inactive">Inactives uniquement</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Agencies Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateurs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Créée le
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {agencies.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    {hasActiveFilters ? 'Aucune agence trouvée avec ces filtres' : 'Aucune agence pour le moment'}
                  </td>
                </tr>
              ) : (
                agencies.map((agency) => (
                  <tr
                    key={agency.id}
                    className="hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-sm">
                            {agency.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {agency.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {agency.city}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{agency.email}</div>
                      {agency.phone && (
                        <div className="text-sm text-gray-500">{agency.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {agency.leadsCount} leads
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {agency.usersCount} utilisateurs
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge active={agency.active} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(agency.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Link
                          href={`/admin/agencies/${agency.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        >
                          Voir
                        </Link>
                        <Link
                          href={`/admin/agencies/${agency.id}/edit`}
                          className="text-gray-600 hover:text-gray-800 font-medium transition-colors"
                        >
                          Modifier
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-4 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => router.get('/admin/agencies', { ...filters, page: pagination.currentPage - 1 })}
                  disabled={pagination.currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Précédent
                </button>
                <button
                  onClick={() => router.get('/admin/agencies', { ...filters, page: pagination.currentPage + 1 })}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Suivant
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-semibold text-gray-900">{pagination.currentPage}</span> sur{' '}
                    <span className="font-semibold text-gray-900">{pagination.totalPages}</span>
                    <span className="mx-2">•</span>
                    <span className="font-semibold text-gray-900">{pagination.totalCount}</span> agence{pagination.totalCount > 1 ? 's' : ''} au total
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => router.get('/admin/agencies', { ...filters, page: pagination.currentPage - 1 })}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => router.get('/admin/agencies', { ...filters, page: pagination.currentPage + 1 })}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Suivant
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

// Stats Card Component
function StatsCard({ title, value, color = 'blue' }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-sm font-medium text-gray-600">{title}</h3>
      <div className="text-3xl font-bold text-gray-900 mt-2">{value}</div>
    </div>
  )
}

// Status Badge Component
function StatusBadge({ active }) {
  if (active) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <span className="w-2 h-2 mr-1 bg-green-400 rounded-full"></span>
        Active
      </span>
    )
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      <span className="w-2 h-2 mr-1 bg-red-400 rounded-full"></span>
      Inactive
    </span>
  )
}
